/**
 * VehiclesDashboardPage - Página do dashboard do módulo Veículos
 *
 * Mesmo padrão dos dashboards do sistema (CRM/Indicações): cabeçalho com
 * título, atualizar e filtros; abas via DashboardTabs; conteúdo via
 * DashboardRenderer. Realtime centralizado aqui para invalidar o cache.
 *
 * @version 1.2.0
 * RELEASE: 2026-10-03 v1.2.0 - Filtros da aba Vistoria (Tipo de Checklist, Modelo e
 *          Responsável) sobem para a linha do cabeçalho, visíveis só nessa aba, com
 *          botão "Mais" (Sheet) quando não couberem na linha.
 * RELEASE: 2026-10-03 v1.1.0 - Centraliza também a atualização em tempo real
 *          dos indicadores da nova aba Vistoria.
 * RELEASE: 2026-10-03 v1.0.1 - Uniformiza o tamanho do filtro de período.
 * RELEASE: 2026-10-03 v1.0.0 - Criação inicial (aba Geral)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Car, Clock, LayoutDashboard, Loader2, RefreshCw, SlidersHorizontal } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ChecklistTypeFilter } from '@/components/checklist/filters/ChecklistTypeFilter';
import { useInspectionTemplatesForSelection } from '@/hooks/useVehicleInspections';
import type { VehiclesInspectionsDashboardFilters } from '@/hooks/useVehiclesInspectionsDashboardOverview';
import { useModuleDashboards } from '@/hooks/useDashboards';
import { DashboardRenderer, DashboardTabs } from '@/components/crm/dashboard';
import { CrmPeriodFilter } from '@/components/crm/filters/CrmPeriodFilter';
import { calculatePeriodDates, type PeriodFilter, type PeriodPreset } from '@/hooks/useCrmOverviewFilters';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { VehicleStatusSelect } from '@/components/vehicles/VehicleStatus';
import { useRefreshWithTimestamp } from '@/hooks/useRefreshWithTimestamp';
import { useVehiclesModule } from '@/hooks/useVehiclesModule';
import { useAuth } from '@/hooks/useAuth';
import { VEHICLE_TYPE_LABELS, type VehicleType } from '@/hooks/useCustomerVehicles';
import { VEHICLES_DASHBOARD_QUERY_KEY, type VehiclesDashboardFilters } from '@/hooks/useVehiclesDashboardOverview';
import { VEHICLES_INSPECTIONS_DASHBOARD_QUERY_KEY } from '@/hooks/useVehiclesInspectionsDashboardOverview';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

const FILTER_TRIGGER = 'h-[22px] w-auto min-w-[110px] gap-1 px-2 text-[11px]';

export default function VehiclesDashboardPage() {
  const queryClient = useQueryClient();
  const { currentTenant } = useAuth();
  const { activeCategories } = useVehiclesModule();
  const { isRefreshing, formattedTime, triggerRefresh } = useRefreshWithTimestamp();

  // Filtros do cabeçalho (período padrão: mês atual)
  const [period, setPeriod] = useState<PeriodFilter>(() => ({ preset: 'month', ...calculatePeriodDates('month') }));
  const [vehicleType, setVehicleType] = useState<VehicleType | null>(null);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [statusId, setStatusId] = useState<string | null>(null);

  // v1.2.0: filtros específicos da aba Vistoria (estado elevado para o cabeçalho)
  const [typeId, setTypeId] = useState('all');
  const [templateId, setTemplateId] = useState('all');
  const [assigneeMode, setAssigneeMode] = useState('all');
  const [moreOpen, setMoreOpen] = useState(false);

  const vehicleFilters = useMemo<VehiclesDashboardFilters>(
    () => ({ startDate: period.startDate, endDate: period.endDate, vehicleType, categoryId, statusId }),
    [period, vehicleType, categoryId, statusId],
  );

  const { dashboards, selectedDashboard, selectedDashboardId, selectDashboard, isLoading } = useModuleDashboards('vehicles');
  const isInspectionsTab = selectedDashboard?.component_key === 'vehicles_inspections_overview';

  // Mesma fonte da criação de vistorias: só modelos publicados, filtrados pelo tipo.
  const { data: templates = [] } = useInspectionTemplatesForSelection(isInspectionsTab);
  const visibleTemplates = useMemo(
    () => typeId === 'all' ? templates : templates.filter((t) => t.type_id === typeId),
    [templates, typeId],
  );

  /** Na aba Vistoria, os filtros específicos viajam junto dos filtros de veículo. */
  const rendererFilters = useMemo<VehiclesDashboardFilters | VehiclesInspectionsDashboardFilters>(() => (
    isInspectionsTab ? {
      ...vehicleFilters,
      checklistTypeId: typeId === 'all' ? null : typeId,
      templateId: templateId === 'all' ? null : templateId,
      assigneeMode: assigneeMode === 'all' ? null : assigneeMode as 'user' | 'queue' | 'owner' | 'provider',
    } : vehicleFilters
  ), [isInspectionsTab, vehicleFilters, typeId, templateId, assigneeMode]);
  const inspectionActiveCount = [typeId, templateId, assigneeMode].filter((v) => v !== 'all').length;

  /** Controles da aba Vistoria, reutilizados na linha e no painel "Mais". */
  const renderInspectionFilters = (wide = false) => (
    <>
      <ChecklistTypeFilter value={typeId} onChange={(v) => { setTypeId(v); setTemplateId('all'); }} className={wide ? 'h-9 w-full' : 'h-[22px] w-[150px] text-[11px]'} />
      <Select value={templateId} onValueChange={setTemplateId}>
        <SelectTrigger className={wide ? 'h-9 w-full text-sm' : FILTER_TRIGGER}><SelectValue placeholder="Modelo de vistoria" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todos os modelos</SelectItem>
          {visibleTemplates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={assigneeMode} onValueChange={setAssigneeMode}>
        <SelectTrigger className={wide ? 'h-9 w-full text-sm' : FILTER_TRIGGER}><SelectValue placeholder="Responsável" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todos os responsáveis</SelectItem>
          <SelectItem value="user">Usuário interno</SelectItem>
          <SelectItem value="queue">Fila global</SelectItem>
          <SelectItem value="owner">Cliente/proprietário</SelectItem>
          <SelectItem value="provider">Prestador</SelectItem>
        </SelectContent>
      </Select>
    </>
  );

  const invalidate = useCallback(
    async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [VEHICLES_DASHBOARD_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [VEHICLES_INSPECTIONS_DASHBOARD_QUERY_KEY] }),
      ]);
    },
    [queryClient],
  );

  const handleRefresh = useCallback(async () => {
    await triggerRefresh(async () => { await invalidate(); });
  }, [triggerRefresh, invalidate]);

  // Realtime único da página: frota e vistorias invalidam seus caches sem canais duplicados.
  useEffect(() => {
    if (!currentTenant?.id) return;
    const filter = `tenant_id=eq.${currentTenant.id}`;
    const channel = supabase
      .channel(`vehicles-dashboard-${currentTenant.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'customer_vehicles', filter }, () => invalidate())
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'vehicle_activity_log', filter }, () => invalidate())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'checklist_instances', filter }, () => invalidate())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'checklist_answers', filter }, () => invalidate())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'checklist_answer_revisions', filter }, () => invalidate())
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'checklist_events', filter }, () => invalidate())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [currentTenant?.id, invalidate]);

  if (isLoading) {
    return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }

  if (dashboards.length === 0) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4">
        <LayoutDashboard className="h-16 w-16 text-muted-foreground/50" />
        <h1 className="text-2xl font-bold text-foreground">Dashboard de Veículos</h1>
        <p className="max-w-md text-center text-muted-foreground">
          Nenhum dashboard disponível. Verifique suas permissões de acesso em Permissões Dashboard.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Cabeçalho: título + atualizar | filtros */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex shrink-0 items-center gap-1.5">
          <Car className="h-5 w-5 text-primary" />
          <h1 className="text-lg font-bold tracking-tight text-foreground md:text-2xl">Dashboard de Veículos</h1>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleRefresh} disabled={isRefreshing} title="Atualizar">
            <RefreshCw className={cn('h-3.5 w-3.5', isRefreshing && 'animate-spin')} />
          </Button>
          <span className="hidden items-center gap-1 text-[10px] text-muted-foreground md:inline-flex">
            <Clock className="h-2.5 w-2.5" />{formattedTime}
          </span>
        </div>
        <span className="shrink-0 text-muted-foreground/30">|</span>
        <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto scrollbar-none">
          <CrmPeriodFilter
            className="h-[22px] px-2 text-[11px]"
            value={period}
            onPresetChange={(p: PeriodPreset) => setPeriod({ preset: p, ...calculatePeriodDates(p) })}
            onCustomChange={(s, e) => setPeriod({ preset: 'custom', startDate: s, endDate: e })}
          />
          <Select value={vehicleType ?? 'ALL'} onValueChange={(v) => setVehicleType(v === 'ALL' ? null : (v as VehicleType))}>
            <SelectTrigger className={FILTER_TRIGGER}><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todos os tipos</SelectItem>
              {(Object.keys(VEHICLE_TYPE_LABELS) as VehicleType[]).map((t) => (
                <SelectItem key={t} value={t}>{VEHICLE_TYPE_LABELS[t]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={categoryId ?? 'ALL'} onValueChange={(v) => setCategoryId(v === 'ALL' ? null : v)}>
            <SelectTrigger className={FILTER_TRIGGER}><SelectValue placeholder="Categoria" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todas as categorias</SelectItem>
              {(activeCategories ?? []).map((cat: { id: string; name: string }) => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <VehicleStatusSelect allowAll value={statusId} onChange={setStatusId} className={FILTER_TRIGGER} />
          {/* Telas largas: filtros da aba Vistoria na mesma linha */}
          {isInspectionsTab && <div className="hidden shrink-0 items-center gap-1.5 2xl:flex">{renderInspectionFilters()}</div>}
          {/* Telas menores: botão "Mais" (padrão ListPageFiltersBar) abre o painel lateral */}
          {isInspectionsTab && (
            <Button variant="outline" size="sm" onClick={() => setMoreOpen(true)} className="h-[22px] shrink-0 gap-1 rounded-full px-2 text-[10px] 2xl:hidden">
              <SlidersHorizontal className="h-3 w-3" /><span>Mais</span>
              {inspectionActiveCount > 0 && <Badge variant="secondary" className="h-4 min-w-[16px] rounded-full border-0 bg-primary/15 px-1 text-[10px] text-primary">{inspectionActiveCount}</Badge>}
            </Button>
          )}
        </div>
      </div>

      <DashboardTabs dashboards={dashboards} selectedDashboardId={selectedDashboardId} onSelectDashboard={selectDashboard} alwaysShow />

      {selectedDashboard && (
        <DashboardRenderer componentKey={selectedDashboard.component_key} dashboardId={selectedDashboard.id} vehicleFilters={rendererFilters} />
      )}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="right" className="w-full sm:max-w-sm">
          <SheetHeader><SheetTitle>Filtros da Vistoria</SheetTitle></SheetHeader>
          <div className="mt-4 flex flex-col gap-3">{renderInspectionFilters(true)}</div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
